// All enum definitions for the TMS

// ============================================================================
// LOAD ENUMS
// ============================================================================

export enum LoadStatus {
  CREATED = 'created',
  AVAILABLE = 'available',
  DISPATCHED = 'dispatched',
  EN_ROUTE_PICKUP = 'en_route_pickup',
  AT_PICKUP = 'at_pickup',
  LOADING = 'loading',
  IN_TRANSIT = 'in_transit',
  AT_DELIVERY = 'at_delivery',
  UNLOADING = 'unloading',
  DELIVERED = 'delivered',
  INVOICED = 'invoiced',
  PAID = 'paid',
  CANCELLED = 'cancelled',
}

export enum EquipmentType {
  DRY_VAN = 'dry_van',
  REEFER = 'reefer',
  FLATBED = 'flatbed',
  STEP_DECK = 'step_deck',
  LOWBOY = 'lowboy',
  TANKER = 'tanker',
  HOPPER = 'hopper',
  INTERMODAL = 'intermodal',
  POWER_ONLY = 'power_only',
}

export enum AppointmentType {
  APPOINTMENT = 'Appt',
  FCFS = 'FCFS',
  WINDOW = 'window',
  OPEN = 'open',
}

export enum StopType {
  PICKUP = 'pickup',
  DELIVERY = 'delivery',
  LIVE_LOAD = 'live_load',
  LIVE_UNLOAD = 'live_unload',
  DROP_HOOK = 'drop_hook',
}

export enum StopStatus {
  OPEN = 'OPEN',
  COVERED = 'COVERED',
  ARRIVED = 'ARRIVED',
  PICKED_UP = 'PICKED_UP',
  EMPTY = 'EMPTY',
}

export enum LoadingType {
  LIVE = 'live',
  HOOK = 'hook',
  DROP = 'drop',
  DROP_HOOK = 'drop_hook',
}

export enum HazmatClass {
  EXPLOSIVES = '1',
  FLAMMABLE_GAS = '2.1',
  NON_FLAMMABLE_GAS = '2.2',
  POISON_GAS = '2.3',
  FLAMMABLE_LIQUID = '3',
  FLAMMABLE_SOLID = '4.1',
  SPONTANEOUSLY_COMBUSTIBLE = '4.2',
  DANGEROUS_WHEN_WET = '4.3',
  OXIDIZER = '5.1',
  ORGANIC_PEROXIDE = '5.2',
  POISON_TOXIC = '6.1',
  INFECTIOUS_SUBSTANCE = '6.2',
  RADIOACTIVE = '7',
  CORROSIVE = '8',
  MISCELLANEOUS = '9',
}

export const HAZMAT_CLASS_LABELS: Record<HazmatClass, { label: string; description: string }> = {
  [HazmatClass.EXPLOSIVES]: { label: '1', description: 'Explosives' },
  [HazmatClass.FLAMMABLE_GAS]: { label: '2.1', description: 'Flammable Gas' },
  [HazmatClass.NON_FLAMMABLE_GAS]: { label: '2.2', description: 'Non-Flammable Gas' },
  [HazmatClass.POISON_GAS]: { label: '2.3', description: 'Poison Gas' },
  [HazmatClass.FLAMMABLE_LIQUID]: { label: '3', description: 'Flammable Liquid' },
  [HazmatClass.FLAMMABLE_SOLID]: { label: '4.1', description: 'Flammable Solid' },
  [HazmatClass.SPONTANEOUSLY_COMBUSTIBLE]: { label: '4.2', description: 'Spontaneously Combustible' },
  [HazmatClass.DANGEROUS_WHEN_WET]: { label: '4.3', description: 'Dangerous When Wet' },
  [HazmatClass.OXIDIZER]: { label: '5.1', description: 'Oxidizer' },
  [HazmatClass.ORGANIC_PEROXIDE]: { label: '5.2', description: 'Organic Peroxide' },
  [HazmatClass.POISON_TOXIC]: { label: '6.1', description: 'Poison/Toxic' },
  [HazmatClass.INFECTIOUS_SUBSTANCE]: { label: '6.2', description: 'Infectious Substance' },
  [HazmatClass.RADIOACTIVE]: { label: '7', description: 'Radioactive' },
  [HazmatClass.CORROSIVE]: { label: '8', description: 'Corrosive' },
  [HazmatClass.MISCELLANEOUS]: { label: '9', description: 'Miscellaneous Dangerous Goods' },
};

// ============================================================================
// DRIVER ENUMS
// ============================================================================

export enum DriverStatus {
  APPLICANT = 'applicant',
  PENDING_APPROVAL = "pending_approval",
  ONBOARDING = 'onboarding',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  ON_LEAVE = 'on_leave',
  TERMINATED = 'terminated',
  PENDING = 'pending',
}

export enum DriverType {
  COMPANY = 'company',
  OWNER_OPERATOR = 'owner_operator',
  LEASE = 'lease',
}

// Tax classification for drivers (W2 vs 1099)
export enum DriverTaxClassification {
  W2 = 'w2',           // Employee - taxes withheld by company
  C1099 = '1099',      // Independent contractor - responsible for own taxes
}

export enum PayType {
  PER_MILE = 'per_mile',
  PERCENTAGE = 'percentage',
  HOURLY = 'hourly',
  SALARY = 'salary',
  FLAT = 'flat',
}

// Rate add-on types for driver compensation
export enum DriverRateAddOnType {
  FLAT_FEE = 'flat_fee',           // Fixed amount per load
  EXTRA_STOP = 'extra_stop',       // Additional stop fee
  DETENTION = 'detention',         // Detention pay
  LAYOVER = 'layover',             // Layover pay
  TARP = 'tarp',                   // Tarp pay (flatbed)
  HAZMAT = 'hazmat',               // Hazmat pay
  NYC_BONUS = 'nyc_bonus',         // NYC delivery bonus
  WEEKEND_BONUS = 'weekend_bonus', // Weekend delivery bonus
  OTHER = 'other',                 // Other add-ons
}

export enum CDLClass {
  A = 'A',
  B = 'B',
  C = 'C'
}

export enum CDLEndorsement {
  H = 'H',  // Hazmat
  N = 'N',  // Tank
  P = 'P',  // Passenger
  S = 'S',  // School Bus
  T = 'T',  // Doubles/Triples
  X = 'X',  // Hazmat + Tank
}

export enum HOSStatus {
  OFF_DUTY = 'off_duty',
  SLEEPER = 'sleeper',
  DRIVING = 'driving',
  ON_DUTY = 'on_duty',
  PERSONAL_CONVEYANCE = 'personal_conveyance',
  YARD_MOVE = 'yard_move',
}

// ============================================================================
// TRUCK ENUMS
// ============================================================================

export enum TruckStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  IN_SHOP = 'in_shop',
  OUT_OF_SERVICE = 'out_of_service',
  SOLD = 'sold'
}

export enum TruckType {
  DAY_CAB = 'day_cab',
  SLEEPER_SMALL = 'sleeper_small',
  SLEEPER_LARGE = 'sleeper_large',
  HOTSHOT = 'hotshot',
  STRAIGHT = 'straight',
  SPRINTER = 'sprinter'
}

// ============================================================================
// TRAILER ENUMS
// ============================================================================

export enum TrailerStatus {
  PENDING = 'pending',
  ACTIVE = 'active',
  IN_SHOP = 'in_shop',
  OUT_OF_SERVICE = 'out_of_service',
  SOLD = 'sold',
  DROPPED = 'dropped'
}

export enum TrailerType {
  DRY_VAN = 'dry_van',
  REEFER = 'reefer',
  FLATBED = 'flatbed',
  STEP_DECK = 'step_deck',
  LOWBOY = 'lowboy',
  CONESTOGA = 'conestoga',
  TANKER = 'tanker',
  HOPPER = 'hopper',
  CONTAINER = 'container',
  CHASSIS = 'chassis',
  DUMP = 'dump',
  AUTO_CARRIER = 'auto_carrier'
}

// ============================================================================
// CUSTOMER ENUMS
// ============================================================================

export enum CustomerType {
  SHIPPER = 'shipper',
  BROKER = 'broker',
  CONSIGNEE = 'consignee',
  FREIGHT_FORWARDER = 'freight_forwarder',
}

export enum CustomerStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  SUSPENDED = 'suspended',
  PENDING = 'pending',
}

export enum PaymentTerms {
  COD = 'COD',
  NET15 = 'NET15',
  NET30 = 'NET30',
  NET45 = 'NET45',
  NET60 = 'NET60',
  PREPAID = 'PREPAID',
}

// ============================================================================
// INVOICE ENUMS
// ============================================================================

export enum InvoiceStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  SENT = 'sent',
  PAID = 'paid',
  VOIDED = 'voided',
}

export enum PaymentMethod {
  CHECK = 'check',
  ACH = 'ach',
  WIRE = 'wire',
  CREDIT_CARD = 'credit_card',
  CASH = 'cash',
  FACTORING = 'factoring',
  OTHER = 'other',
}

export enum PaymentStatus {
  PENDING = 'pending',
  APPLIED = 'applied',
  PARTIAL = 'partial',
  VOIDED = 'voided',
}

// ============================================================================
// SETTLEMENT ENUMS
// ============================================================================

export enum SettlementStatus {
  INCOMPLETE = 'incomplete',
  GENERATED = 'generated',
  REVIEWED = 'reviewed',
  PAID = 'paid',
  VOIDED = 'voided',
}

// ============================================================================
// EXPENSE ENUMS
// ============================================================================

export enum ExpenseCategory {
  FUEL = 'fuel',
  MAINTENANCE = 'maintenance',
  REPAIR = 'repair',
  INSURANCE = 'insurance',
  TOLLS = 'tolls',
  PERMITS = 'permits',
  PARKING = 'parking',
  SCALE = 'scale',
  LUMPER = 'lumper',
  DETENTION = 'detention',
  DRIVER_ADVANCE = 'driver_advance',
  DRIVER_PAY = 'driver_pay',
  OFFICE = 'office',
  EQUIPMENT = 'equipment',
  LEASE = 'lease',
  TAXES = 'taxes',
  OTHER = 'other',
}

// ============================================================================
// DOCUMENT ENUMS
// ============================================================================

export enum DocumentCategory {
  BOL = 'bol',
  POD = 'pod',
  RATE_CONFIRMATION = 'rate_confirmation',
  INVOICE = 'invoice',
  CDL = 'cdl',
  MEDICAL_CARD = 'medical_card',
  MVR = 'mvr',
  REGISTRATION = 'registration',
  INSURANCE = 'insurance',
  PERMIT = 'permit',
  INSPECTION = 'inspection',
  CONTRACT = 'contract',
  OTHER = 'other',
}

export enum DocumentStatus {
  PENDING = 'pending',
  VERIFIED = 'verified',
  REJECTED = 'rejected',
  EXPIRED = 'expired',
}

// ============================================================================
// USER ENUMS
// ============================================================================

export enum UserRole {
  ADMIN = 'admin',
  DISPATCHER = 'dispatcher',
  ACCOUNTING = 'accounting',
  COMPLIANCE = 'compliance',
  DRIVER = 'driver',
  VIEWER = 'viewer',
  CUSTOMER_PORTAL = 'customer_portal',
  FUEL_OPERATOR = 'fuel_operator',
  YARD_JOCKEY = 'yard_jockey',
  MAINTENANCE_OPERATOR = 'maintenance_operator',
  MAINTENANCE_TECHNICIAN = 'maintenance_technician',
}

/**
 * Roles that belong to the maintenance portal.
 * Users with ONLY these roles are auto-routed to the maintenance view.
 */
export const MAINTENANCE_ROLES: string[] = [
  UserRole.YARD_JOCKEY,
  UserRole.MAINTENANCE_OPERATOR,
  UserRole.MAINTENANCE_TECHNICIAN,
];

export enum UserStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING = 'pending',
  LOCKED = 'locked',
}
